/**
 * useUnifiedSearch — peace-hub-port (Block 6.F.2 §3.2).
 *
 * §m Q-S5 empirical outcome (revising the §3.9 r3 prescription):
 * `// @ts-check` directive was attempted on this file; surfaced 15+
 * implicit-any errors on internal callback params (filter / map /
 * synonym-match closures densely chained per peace-hub idiom). Cost
 * to author full JSDoc was high relative to the type-safety win on
 * internal-only types. Decision: useNavigation.js stayed @ts-checked
 * (lightweight; clean fit; that hook has since been deleted as dead
 * code along with the never-mounted 6.F.1 TenantApp stub);
 * useUnifiedSearch.js does NOT carry the directive. Consumer-facing return shape documented via JSDoc on
 * the export below; internals run as plain JS. The Q-S5 outcome
 * resolves: per-file opt-in retained as the rule, with hooks earning
 * the directive ONLY when callback-density doesn't dominate authoring
 * cost. Plan §3.9 r3 framing holds; this file is a measured exception.
 *
 * Adapted port of peace-hub/src/hooks/useUnifiedSearch.js with:
 *   - Data sources: TanStack Query against peace-os bulk-load endpoints
 *     (api.fetchAllProvidersForSearch + fetchAllIndividualsForSearch);
 *     staleTime=5min mirrors peace-hub session-storage CACHE_TTL.
 *   - Notes: no separate notesByOrg map. Notes live INSIDE
 *     ProviderForSearch.notes (string field). getNotesForResource
 *     returns the org's .notes directly.
 *   - Search-logging effect: dropped (peace-os has no analytics
 *     endpoint; Era B re-adds via App Insights).
 *   - H1 — contactFuse `specialties` weight: operates on `string | null`
 *     not array. fuse handles both natively. Synonym-expanded specialty
 *     match adapts `c.specialties?.some()` → `.toLowerCase().includes()`.
 *   - M5 — counties is FILTER + DISPLAY field, NOT a fuse search dim.
 *     orgFuse retains 6 keys (name/notes/contacts/_contactNames/
 *     subcategories/specialties); counties consumed by filter
 *     application + FilterBar cascade + ResourceCard display only.
 *   - parseQuery's filterOptions cascade derived from raw resources
 *     (single SoT) instead of preloaded JSON.
 *
 * Verbatim from peace-hub:
 *   - Fuse weight blocks (orgFuse + contactFuse + sub-Fuse) per §3.2 inline.
 *   - Smart-query parsing (multi-word + single-word + synonym expansion).
 *   - mergeContacts (multi-org affiliation merging via current_org_ids).
 *   - orgResults memo (Pass 1 literal + Pass 2 smart-parse + filter application).
 *   - contactResults memo (fuzzy + org-discovery + synonym specialties).
 *
 * @typedef {{slug: string, display_name: string}} LookupRef
 * @typedef {import('@/lib/types').ProviderForSearch} ProviderForSearch
 * @typedef {import('@/lib/types').IndividualForSearch} IndividualForSearch
 * @typedef {ProviderForSearch & {_contactNames: string}} OrgWithContacts
 *
 * @typedef {{
 *   categories?: string[],
 *   subcategories?: string[],
 *   specialties?: string[],
 *   highlights?: string[],
 *   counties?: string[],
 *   medicaid?: string[],
 *   dementiaUnit?: boolean,
 * }} FilterState
 */

import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { useTenant } from '@/lib/auth'
import { api } from '@/lib/api'
import { expandQuery } from '@/lib/searchSynonyms'
import {
  RESTRICTIONS_LABELS as RESTRICTIONS_LABEL_LIST,
  MEMORY_CARE_LABEL,
  HOME_CARE_TYPE_LABELS,
  RESPITE_DISPLAY_LABEL,
} from '@/lib/facetTaxonomy'

const STALE_TIME = 5 * 60 * 1000

// ── Fuse configurations ─────────────────────────────────────────────────────
function buildOrgFuse(data) {
  return new Fuse(data, {
    keys: [
      { name: 'name', weight: 0.45 },
      { name: 'notes', weight: 0.1 },
      { name: 'contacts', weight: 0.1 },
      { name: '_contactNames', weight: 0.15 },
      { name: 'subcategories.display_name', weight: 0.1 },
      { name: 'specialties.display_name', weight: 0.1 },
    ],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 2,
  })
}

function buildContactFuse(data) {
  return new Fuse(data, {
    keys: [
      { name: 'full_name', weight: 0.55 },
      { name: 'title', weight: 0.10 },
      { name: 'current_org_names', weight: 0.15 },
      { name: 'specialties', weight: 0.10 },
      { name: 'notes', weight: 0.05 },
    ],
    threshold: 0.2,
    ignoreLocation: true,
    minMatchCharLength: 2,
  })
}

// Token-AND (subset) matcher for contact search. Splits the query on
// whitespace and requires EVERY token to be a substring of the contact's
// normalized identity text (full_name + title + affiliated org names +
// specialties). Order-insensitive and tolerant of interstitial tokens, so it
// catches the two cases Fuse's tight fuzzy threshold (0.2) drops:
//   - middle initials: "Maria Cestone"  matches "Maria A Cestone" (scores
//     0.55 > 0.2 under Fuse → filtered out)
//   - word reordering: "cestone maria"  matches "Maria A Cestone"
// This is UNIONed with (not a replacement for) the Fuse results, which still
// supply single-typo fuzziness. Mirrors the org-name substring approach
// already used for org-discovery in the contactResults memo below.
//
// @param {string} query
// @param {IndividualForSearch} contact
// @returns {boolean}
export function contactMatchesAllTokens(query, contact) {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return false
  const haystack = [
    contact.full_name,
    contact.title,
    ...(contact.current_org_names ?? []),
    contact.specialties,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return tokens.every((t) => haystack.includes(t))
}

// ── Multi-word term lookup for smart query parsing ──────────────────────────
// Built from raw resources at hook init. peace-hub preloaded
// filterOptions.json; peace-os derives from the bulk-load.
function buildMultiWordTerms(resources) {
  const terms = []
  const subSet = new Set()
  const specSet = new Set()
  const countySet = new Set()
  for (const r of resources) {
    for (const s of r.subcategories) subSet.add(s.display_name)
    for (const s of r.specialties) specSet.add(s.display_name)
    for (const c of r.counties) countySet.add(c)
  }
  for (const term of subSet) {
    if (term.toLowerCase().includes(' ')) {
      terms.push({ term: term.toLowerCase(), original: term, type: 'subcategory' })
    }
  }
  for (const term of specSet) {
    if (term.toLowerCase().includes(' ')) {
      terms.push({ term: term.toLowerCase(), original: term, type: 'specialty' })
    }
  }
  for (const term of countySet) {
    if (term.toLowerCase().includes(' ')) {
      terms.push({ term: term.toLowerCase(), original: term, type: 'county' })
    }
  }
  terms.sort((a, b) => b.term.length - a.term.length)
  return terms
}

function buildFilterOptions(resources) {
  const subs = new Set()
  const specs = new Set()
  const counts = new Set()
  const cats = new Set()
  for (const r of resources) {
    cats.add(r.category_slug)
    for (const s of r.subcategories) subs.add(s.display_name)
    for (const s of r.specialties) specs.add(s.display_name)
    for (const c of r.counties) counts.add(c)
  }
  return { categories: [...cats], subcategories: [...subs], specialties: [...specs], counties: [...counts] }
}

// ── Query parser (with synonym expansion) ────────────────────────────────────
function parseQuery(raw, multiWordTerms, filterOptions) {
  const { expandedTerms, originalFreeText } = expandQuery(raw)

  let query = originalFreeText.toLowerCase().trim()
  query = query.replace(/\b(in|near|around|the|for|a|an|with|and|or)\b/g, ' ').trim()

  const exact = { counties: [], categories: [], subcategories: [], specialties: [] }

  for (const term of expandedTerms) {
    const termLower = term.toLowerCase()
    let matched = false
    for (const sub of filterOptions.subcategories) {
      if (sub.toLowerCase() === termLower) { exact.subcategories.push(sub); matched = true; break }
    }
    if (!matched) {
      for (const spec of filterOptions.specialties) {
        if (spec.toLowerCase() === termLower) { exact.specialties.push(spec); matched = true; break }
      }
    }
    if (!matched) {
      for (const cat of filterOptions.categories) {
        if (cat.toLowerCase() === termLower) { exact.categories.push(cat); matched = true; break }
      }
    }
    if (!matched) {
      query = (query + ' ' + term).trim()
    }
  }

  for (const { term, original, type } of multiWordTerms) {
    if (query.includes(term)) {
      if (type === 'county') exact.counties.push(original)
      else if (type === 'subcategory') exact.subcategories.push(original)
      else if (type === 'specialty') exact.specialties.push(original)
      query = query.replace(term, ' ').trim()
    }
  }

  const remaining = query.split(/\s+/).filter(Boolean)
  const freeText = []

  for (const word of remaining) {
    let matched = false

    for (const county of filterOptions.counties) {
      if (county.toLowerCase() === word) { exact.counties.push(county); matched = true; break }
    }
    if (matched) continue

    for (const sub of filterOptions.subcategories) {
      if (sub.toLowerCase() === word) { exact.subcategories.push(sub); matched = true; break }
    }
    if (matched) continue

    for (const spec of filterOptions.specialties) {
      if (spec.toLowerCase() === word) { exact.specialties.push(spec); matched = true; break }
    }
    if (matched) continue

    for (const cat of filterOptions.categories) {
      if (cat.toLowerCase() === word) { exact.categories.push(cat); matched = true; break }
    }
    if (matched) continue

    if (word === 'county' || word === 'counties') continue
    freeText.push(word)
  }

  return { exact, freeText: freeText.join(' ').trim(), expandedTerms }
}

// ── Merge contacts into orgs (supports multi-org contacts) ───────────────────
function mergeContacts(orgs, individuals) {
  const contactsByOrg = {}
  for (const ind of individuals) {
    for (const orgId of ind.current_org_ids) {
      if (!contactsByOrg[orgId]) contactsByOrg[orgId] = []
      contactsByOrg[orgId].push(ind.full_name)
    }
  }
  return orgs.map((org) => {
    const orgContactNames = contactsByOrg[org.id] || []
    return { ...org, _contactNames: orgContactNames.join(', ') }
  })
}

// ── Sort ordering ───────────────────────────────────────────────────────────
// Restores the prototype's directory "Sort" dropdown (directory.jsx DIR_SORTS):
//   - 'recommended' (default): recommended-first, then A-Z. Stable-partition so
//     the incoming order (alpha when browsing, fuse relevance when searching) is
//     preserved WITHIN each group. Organizations and people both carry
//     is_recommended (0072 / 0197).
//   - 'alpha': A-Z by the provided name accessor.
//   - 'county': by county (alphabetical), then A-Z by name. Contacts carry no
//     county (IndividualForSearch has none), so getCounty returns '' for them →
//     they collapse to A-Z (no invented data).
function firstCounty(counties) {
  return [...(counties ?? [])].sort((a, b) => a.localeCompare(b))[0] ?? ''
}

function reorderBySort(items, sortMode, getName, getCounty) {
  if (sortMode === 'alpha') {
    return [...items].sort((a, b) => getName(a).localeCompare(getName(b)))
  }
  if (sortMode === 'county') {
    return [...items].sort(
      (a, b) => getCounty(a).localeCompare(getCounty(b)) || getName(a).localeCompare(getName(b)),
    )
  }
  // 'recommended' (default)
  const recommended = []
  const rest = []
  for (const item of items) {
    if (item.is_recommended) recommended.push(item)
    else rest.push(item)
  }
  return recommended.length > 0 ? [...recommended, ...rest] : items
}

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════
/**
 * @param {{ query?: string, viewMode?: string, filters?: object,
 *           visibleCount?: number, sortMode?: string }} [initial]
 *   Starting state — the directory passes what its URL names, so the
 *   first render already shows that search.
 */
export function useUnifiedSearch(initial = {}) {
  const { tenantSlug } = useTenant()

  // Phase 1.8 — the /providers/all endpoint now returns the extended
  // payload (rows + service_area_csas/msas/metro_divisions/zips). We
  // cache the full payload + expose tier-table arrays for client-side
  // city/ZIP/MSA query resolution.
  const orgQuery = useQuery({
    queryKey: ['providers-for-search', tenantSlug],
    queryFn: ({ signal }) => api.fetchAllProvidersForSearchExtended(tenantSlug, signal),
    staleTime: STALE_TIME,
  })
  const indQuery = useQuery({
    queryKey: ['individuals-for-search', tenantSlug],
    queryFn: ({ signal }) => api.fetchAllIndividualsForSearch(tenantSlug, signal).then((r) => r.rows),
    staleTime: STALE_TIME,
  })

  const resources = orgQuery.data?.rows ?? []
  const contacts = indQuery.data ?? []
  const loading = orgQuery.isLoading || indQuery.isLoading
  const error = orgQuery.error ?? indQuery.error
  // Both lists have rows to count. Not the same as !loading && !error: a
  // query paused before its first answer (offline, or a retry held in a
  // hidden tab) is neither loading nor failed. And a refresh that fails over
  // lists already on screen keeps their data, so the counts stay.
  const loaded = orgQuery.data !== undefined && indQuery.data !== undefined

  // Try again on the directory: ask again for the two lists only.
  const refetchOrgs = orgQuery.refetch
  const refetchPeople = indQuery.refetch
  const refetch = useCallback(() => {
    void refetchOrgs()
    void refetchPeople()
  }, [refetchOrgs, refetchPeople])

  // Phase 1.8 — tier-table data for city/ZIP/MSA resolution.
  const zipsData = orgQuery.data?.service_area_zips ?? []
  const msasData = orgQuery.data?.service_area_msas ?? []
  const csasData = orgQuery.data?.service_area_csas ?? []

  // Search + filter state
  const [query, setQuery] = useState(initial.query ?? '')
  const [viewMode, setViewMode] = useState(initial.viewMode ?? 'all')
  const [filters, setFilters] = useState(initial.filters ?? {})
  const [visibleCount, setVisibleCount] = useState(initial.visibleCount ?? 30)
  // Directory sort: 'recommended' (default) | 'alpha' | 'county'.
  const [sortMode, setSortMode] = useState(initial.sortMode ?? 'recommended')

  // Build derived structures (memoized).
  const mergedOrgs = useMemo(() => mergeContacts(resources, contacts), [resources, contacts])
  const orgFuse = useMemo(() => buildOrgFuse(mergedOrgs), [mergedOrgs])
  const contactFuse = useMemo(() => buildContactFuse(contacts), [contacts])
  const filterOptions = useMemo(() => buildFilterOptions(resources), [resources])
  const multiWordTerms = useMemo(() => buildMultiWordTerms(resources), [resources])

  // Phase 1.8 — resolve a query term to a set of county display_names.
  // Three resolution paths:
  //   (a) 5-digit ZIP → service_area_zips lookup → county_display
  //   (b) substring match on zips[].city → distinct county_displays
  //   (c) substring match on msas[].short_name OR display_name
  //       → county_ids → resolved to county display_names via zips JOIN
  // Returns array of county display_names to inject into exact.counties.
  const resolveGeographicTerm = useCallback(
    (rawTerm) => {
      const term = String(rawTerm).trim()
      if (!term) return []
      const lower = term.toLowerCase()
      const counties = new Set()
      // (a) 5-digit ZIP match
      if (/^[0-9]{5}$/.test(term)) {
        const zip = zipsData.find((z) => z.zip_code === term)
        if (zip) counties.add(zip.county_display)
        return [...counties]
      }
      // (b) city substring (only when term length >= 3 to avoid noise)
      if (lower.length >= 3) {
        for (const z of zipsData) {
          if (z.city && z.city.toLowerCase().includes(lower)) {
            counties.add(z.county_display)
          }
        }
        // (c) MSA short_name OR display_name substring
        for (const m of msasData) {
          if (
            m.short_name.toLowerCase().includes(lower) ||
            m.display_name.toLowerCase().includes(lower)
          ) {
            // Expand to all county_ids → county display via zips
            for (const z of zipsData) {
              if (z.msa_id === m.id) counties.add(z.county_display)
            }
          }
        }
        // (c2) CSA short_name OR display_name substring
        for (const c of csasData) {
          if (
            c.short_name.toLowerCase().includes(lower) ||
            c.display_name.toLowerCase().includes(lower)
          ) {
            for (const z of zipsData) {
              if (z.csa_id === c.id) counties.add(z.county_display)
            }
          }
        }
      }
      return [...counties]
    },
    [zipsData, msasData, csasData],
  )

  // ── Organization results ───────────────────────────────────────────────────
  const orgResults = useMemo(() => {
    if (viewMode === 'people') return []

    let items

    if (query.trim().length >= 2) {
      // Pass 1: literal name match.
      const nameMatches = orgFuse.search(query.trim()).map((r) => r.item)
      const matchedIds = new Set(nameMatches.map((r) => r.id))

      // Pass 2: smart-query parser.
      const { exact, freeText } = parseQuery(query, multiWordTerms, filterOptions)

      // Phase 1.8 — geographic resolution of free-text terms.
      // 5-digit ZIP, city substring, MSA short_name, or CSA short_name
      // resolves to one or more county display_names, which extend
      // exact.counties for the smart-filter pass below.
      if (freeText) {
        const geoCounties = resolveGeographicTerm(freeText)
        for (const c of geoCounties) {
          if (!exact.counties.some((ec) => ec.toLowerCase() === c.toLowerCase())) {
            exact.counties.push(c)
          }
        }
      }

      const hasExactFilters =
        exact.counties.length || exact.categories.length ||
        exact.subcategories.length || exact.specialties.length

      let smartItems = []
      if (hasExactFilters) {
        smartItems = [...mergedOrgs]
        if (exact.counties.length) {
          smartItems = smartItems.filter((r) =>
            r.counties.some((c) => exact.counties.some((ec) => c.toLowerCase() === ec.toLowerCase())),
          )
        }
        if (exact.categories.length) {
          smartItems = smartItems.filter((r) =>
            exact.categories.some((ec) => r.category_slug.toLowerCase() === ec.toLowerCase()),
          )
        }
        if (exact.subcategories.length) {
          smartItems = smartItems.filter((r) =>
            r.subcategories.some((s) => exact.subcategories.some((es) => s.display_name.toLowerCase() === es.toLowerCase())),
          )
        }
        if (exact.specialties.length) {
          smartItems = smartItems.filter((r) =>
            r.specialties.some((s) => exact.specialties.some((es) => s.display_name.toLowerCase() === es.toLowerCase())),
          )
        }

        if (freeText) {
          const subFuse = new Fuse(smartItems, {
            keys: [
              { name: 'name', weight: 0.4 },
              { name: 'subcategories.display_name', weight: 0.15 },
              { name: 'specialties.display_name', weight: 0.1 },
              { name: 'notes', weight: 0.1 },
              { name: 'contacts', weight: 0.05 },
              { name: '_contactNames', weight: 0.2 },
            ],
            threshold: 0.3,
            ignoreLocation: true,
            minMatchCharLength: 2,
          })
          smartItems = subFuse.search(freeText).map((r) => r.item)
        }
      }

      const extras = smartItems.filter((r) => !matchedIds.has(r.id))
      items = [...nameMatches, ...extras]
    } else {
      items = [...mergedOrgs].sort((a, b) =>
        a.name.localeCompare(b.name) ||
        ((a.subcategories[0]?.display_name) || '').localeCompare((b.subcategories[0]?.display_name) || '') ||
        ((a.counties[0]) || '').localeCompare((b.counties[0]) || ''),
      )
    }

    // Apply dropdown filters.
    if (filters.categories?.length) {
      items = items.filter((r) => filters.categories.includes(r.category_slug))
    }
    if (filters.subcategories?.length) {
      items = items.filter((r) => r.subcategories.some((s) => filters.subcategories.includes(s.display_name)))
    }
    if (filters.specialties?.length) {
      items = items.filter((r) => r.specialties.some((s) => filters.specialties.includes(s.display_name)))
    }
    // Phase 1.9.1 — Features pill filter (was: Phase 1.9 highlights).
    // Phase 1.9.3 — Restrictions family added as third sub-group.
    // The pill renders as "Features" in the UI; the internal selection
    // key stays `filters.highlights` (minimum-diff). The grouped picker
    // presents three families:
    //   - Medicaid       → matches r.medicaid === <selected label>
    //   - Memory Care    → matches r.dementia_unit === true (single label)
    //   - Restrictions   → matches r.gender_restrictions === <selected label>
    // Semantics: OR within family (any selected hit), AND across families.
    // Labels are unique across families so a flat string[] selection
    // state suffices; we partition here at filter application time.
    if (filters.highlights?.length) {
      const selected = filters.highlights
      // SSOT — facet labels imported from @/lib/facetTaxonomy (same lists the
      // FilterBar pills + the suggest form read). E3: Home Care Type + Offers
      // respite are surfaced under the Features pill but filter against
      // service_delivery_models[] (NOT provider_highlights), so their labels are
      // excluded from the medicaid catch-all below.
      const RESTRICTIONS_LABELS = new Set(RESTRICTIONS_LABEL_LIST)
      const HOME_CARE_LABELS = new Set(HOME_CARE_TYPE_LABELS)
      const RESPITE_LABEL = RESPITE_DISPLAY_LABEL
      const memoryCareSelected = selected.includes(MEMORY_CARE_LABEL)
      const restrictionsSelected = selected.filter((s) => RESTRICTIONS_LABELS.has(s))
      const homeCareSelected = selected.filter((s) => HOME_CARE_LABELS.has(s))
      const respiteSelected = selected.includes(RESPITE_LABEL)
      const medicaidSelected = selected.filter(
        (s) =>
          s !== MEMORY_CARE_LABEL &&
          !RESTRICTIONS_LABELS.has(s) &&
          !HOME_CARE_LABELS.has(s) &&
          s !== RESPITE_LABEL,
      )
      if (medicaidSelected.length > 0) {
        items = items.filter((r) => medicaidSelected.includes(r.medicaid))
      }
      if (memoryCareSelected) {
        items = items.filter((r) => r.dementia_unit === true)
      }
      if (restrictionsSelected.length > 0) {
        items = items.filter((r) => restrictionsSelected.includes(r.gender_restrictions))
      }
      // E3 — Home Care Type (OR within: hourly/24-7/live-in) against the
      // service-models vocab; multi-select preserved.
      if (homeCareSelected.length > 0) {
        items = items.filter((r) =>
          (r.service_delivery_models ?? []).some((m) => homeCareSelected.includes(m)),
        )
      }
      // E3 — "Offers respite" = presence of the 'Respite' service-model value.
      if (respiteSelected) {
        items = items.filter((r) => (r.service_delivery_models ?? []).includes('Respite'))
      }
    }
    // Phase 1.10 — Service Model top-level filter pill operates on the
    // plural `service_delivery_models: string[]` per r2 disposition #11
    // (Path A wire-shape evolution). OR semantic within selected values;
    // a provider matches if ANY of its assigned models intersects the
    // selected set.
    if (filters.service_models?.length) {
      items = items.filter((r) =>
        (r.service_delivery_models ?? []).some((m) => filters.service_models.includes(m)),
      )
    }
    // Payment type pill (provider_organizations.payment_types jsonb string[]).
    // Same partition semantics as service_models: OR within the facet (a
    // provider matches if ANY of its payment_types intersects the selected set),
    // AND across facets (sequential filter).
    if (filters.payment_types?.length) {
      items = items.filter((r) =>
        (r.payment_types ?? []).some((p) => filters.payment_types.includes(p)),
      )
    }
    if (filters.counties?.length) {
      items = items.filter((r) => r.counties.some((c) => filters.counties.includes(c)))
    }
    // E2 (+ follow-up fix) — "Has notes" keys on `has_current_note`, the
    // CURRENT-note signal (a note whose author is still visible in the tenant,
    // mirroring what the detail renders), NOT the all-versions `notes` blob.
    // The blob retains bodies from authors no longer in the tenant for fuzzy
    // search, so filtering on it wrongly included providers whose detail shows
    // "No notes yet" (the note's author left the tenant → the detail's INNER
    // JOIN users drops it).
    if (filters.has_notes) {
      items = items.filter((r) => r.has_current_note === true)
    }
    // "Recommended" keeps only the tenant's recommended organizations
    // (is_recommended); the default sort still floats them first when
    // the toggle is off.
    if (filters.recommended_only) {
      items = items.filter((r) => r.is_recommended === true)
    }

    // Sort dropdown (recommended-first default). Applied as the final ordering
    // step so it composes over both the browse (alpha) and search (relevance)
    // result orders.
    return reorderBySort(items, sortMode, (r) => r.name, (r) => firstCounty(r.counties))
  }, [query, filters, mergedOrgs, orgFuse, viewMode, multiWordTerms, filterOptions, resolveGeographicTerm, sortMode])

  // ── Contact results ────────────────────────────────────────────────────────
  // The facets that describe an organization and have no counterpart on a
  // person. Kept beside the contact filter that reads it.
  const ORG_ONLY_FACETS = [
    'categories',
    'subcategories',
    'counties',
    'highlights',
    'payment_types',
    'service_models',
    'specialties',
  ]

  const contactResults = useMemo(() => {
    if (viewMode === 'organizations') return []

    const q = query.trim()
    let base
    if (!q || q.length < 2) {
      base = [...contacts].sort((a, b) => a.full_name.localeCompare(b.full_name))
    } else {
      const { expandedTerms } = expandQuery(q)
      const resultIds = new Set()
      const results = []

      const addUnique = (items) => {
        for (const item of items) {
          if (!resultIds.has(item.id)) {
            results.push(item)
            resultIds.add(item.id)
          }
        }
      }

      addUnique(contactFuse.search(q).map((r) => r.item))

      // Token-AND subset match — recovers middle-initial and word-order
      // queries that Fuse's 0.2 threshold drops ("Maria Cestone" ≠
      // "Maria A Cestone" under fuzzy scoring). See contactMatchesAllTokens.
      addUnique(contacts.filter((c) => contactMatchesAllTokens(q, c)))

      const qLower = q.toLowerCase()
      const matchingOrgIds = new Set()
      for (const org of resources) {
        if (org.name.toLowerCase().includes(qLower)) {
          matchingOrgIds.add(org.id)
        }
      }
      if (matchingOrgIds.size > 0) {
        const orgContacts = contacts.filter((c) =>
          c.current_org_ids.some((id) => matchingOrgIds.has(id)),
        )
        addUnique(orgContacts)
      }

      // H1: synonym-expanded specialty match. peace-os specialties is a
      // comma-joined string (controlled vocab) not array; substring match.
      if (expandedTerms.length > 0) {
        const specialtyMatches = contacts.filter((c) =>
          expandedTerms.some((t) => c.specialties?.toLowerCase().includes(t.toLowerCase())),
        )
        addUnique(specialtyMatches)
      }

      base = results
    }

    // Individuals-directory facet filters. Mirrors the org FilterBar
    // partition: OR within a facet, AND across facets, and they apply
    // wherever people are shown, the people view and the combined view
    // alike, on the list and on the map.
    //   - Has notes: a team note on the person (has_current_note, computed
    //     server-side as an organization's is) or the person's older
    //     free-text notes field is non-empty.
    //   - Specialties: c.specialties is a comma-joined controlled-vocab string;
    //     a contact matches if ANY of its (trimmed, case-folded) tokens is in
    //     the selected set (filters.contact_specialties holds display-names).
    // Individuals carry is_recommended (0197), so the default sort floats
    // recommended people first the way it does organizations; they carry no
    // county, so the county sort collapses to A-Z for them (getCounty
    // returns ''). The 'all' and 'people' returns both pass through
    // reorderBySort for consistency.
    const sortContacts = (list) => reorderBySort(list, sortMode, (c) => c.full_name, () => '')
    // Recommended is the one facet both directories share (the pill sits
    // on the organization bar and the people bar): while it is on, only
    // recommended people show, in the all view and the people view alike,
    // on the list and on the map.
    // A filter a record cannot carry removes that record. Category,
    // subcategory, county, features, payment type and delivery method are
    // settings an organization has and a person does not, so filtering on
    // one of them leaves no people: an attorney has no Medicaid setting to
    // match, and showing them beside the matching facilities reads as a
    // result.
    if (ORG_ONLY_FACETS.some((key) => (filters[key] ?? []).length > 0)) return []

    let filtered = filters.recommended_only ? base.filter((c) => c.is_recommended === true) : base
    if (filters.has_notes) {
      filtered = filtered.filter((c) => c.has_current_note === true || (c.notes ?? '').trim().length > 0)
    }
    // Referral partner — the manual is_referral_partner flag (individual-
    // only per 0059; the CRM signal, not derived).
    if (filters.is_referral_partner) {
      filtered = filtered.filter((c) => c.is_referral_partner === true)
    }
    if (filters.contact_specialties?.length) {
      const selected = new Set(filters.contact_specialties.map((s) => s.toLowerCase()))
      filtered = filtered.filter((c) =>
        (c.specialties ?? '').split(',').some((t) => {
          const k = t.trim().toLowerCase()
          return k.length > 0 && selected.has(k)
        }),
      )
    }
    return sortContacts(filtered)
  }, [query, contacts, resources, contactFuse, viewMode, filters, sortMode])

  // ── Filter actions ─────────────────────────────────────────────────────────
  const toggleFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const current = prev[key] || []
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
      return { ...prev, [key]: next }
    })
    setVisibleCount(30)
  }, [])

  const toggleBoolFilter = useCallback((key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
    setVisibleCount(30)
  }, [])

  const clearFilter = useCallback((key) => {
    const boolKeys = key === 'dementiaUnit' || key === 'has_notes' || key === 'is_referral_partner' || key === 'recommended_only'
    setFilters((prev) => ({ ...prev, [key]: boolKeys ? false : [] }))
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters({})
    setVisibleCount(30)
  }, [])

  // E2 — hydrate the full filter object at once (used by DirectoryHub to
  // restore filters from the URL at mount, so browser-back returns to the
  // same search results + filters without losing them).
  const hydrateFilters = useCallback((next) => {
    setFilters(next ?? {})
    setVisibleCount(30)
  }, [])

  const setCategoryFilter = useCallback((cat) => {
    setFilters((prev) => ({ ...prev, categories: [cat] }))
    setVisibleCount(30)
  }, [])

  const updateQuery = useCallback((q) => {
    setQuery(q)
    setVisibleCount(30)
  }, [])

  const showMore = useCallback(() => {
    setVisibleCount((prev) => prev + 30)
  }, [])


  const getNotesForResource = useCallback(
    (resourceId) => {
      const found = resources.find((r) => r.id === resourceId)
      return found?.notes ?? null
    },
    [resources],
  )

  const hasActiveFilters = Object.values(filters).some((v) =>
    Array.isArray(v) ? v.length > 0 : !!v,
  )

  return {
    query,
    setQuery: updateQuery,
    viewMode,
    setViewMode,

    orgResults,
    contactResults,
    visibleCount,
    showMore,

    sortMode,
    setSortMode,

    filters,
    toggleFilter,
    toggleBoolFilter,
    clearFilter,
    clearAllFilters,
    setCategoryFilter,
    hydrateFilters,
    hasActiveFilters,

    resources,
    contacts,
    loading,
    error,
    loaded,
    refetch,
    getNotesForResource,
  }
}
